/* Gateway client — the seam between Deck and the real simulation kernel.
 *
 * ── The split, and why it is where it is ────────────────────────────────────
 *
 * Core owns what it simulates: position, course, speed, fuel. The client owns presentation:
 * SIDC, class names, component lists, EMCON, mission assignment. Snapshots therefore carry
 * only the former, and the client *patches* its existing entities by id rather than replacing
 * them. Sending symbology over the wire would put a rendering decision inside the physics
 * engine, which is the coupling AD-06 exists to prevent.
 *
 * ── Degradation is visible, never silent ───────────────────────────────────
 *
 * If the Gateway is unreachable the Deck keeps running on its seeded fixture data and says so
 * in the status bar. That matters more than it sounds: a demo that silently falls back to mock
 * data looks exactly like a working one, right up to the moment somebody asks whether the
 * numbers are real. `connectionState` exists so the answer is always on screen.
 *
 * ── CON-01 ─────────────────────────────────────────────────────────────────
 *
 * The Gateway URL is derived from the page's own host, so the client never reaches an origin
 * the operator did not already load it from.
 */

import type { LiveTrack } from './liveContacts'
import type { ExclusionZone, RefPoint } from '../data/refpoints'
import type { Mission } from '../data/missions'

export type ConnectionState = 'connecting' | 'live' | 'offline'

export interface SnapshotEntity {
  id: string
  designation: string
  side: string
  classRef: string
  kind: string
  domain?: string
  sidc?: string
  dis?: string
  provenance?: string
  emcon?: { radar: 'active' | 'silent'; comms: 'open' | 'restricted' | 'silent' }
  mission?: string
  enduranceHrs?: number
  components?: {
    id: string
    kind: string
    name: string
    health: string
    detail?: string
    rangeNm?: number
    arcHalfWidthDeg?: number
    arcOffsetDeg?: number
    sensorKind?: string
    weaponRangeNm?: number
    rounds?: number
  }[]
  lat: number
  lon: number
  altitudeFt: number
  courseDeg: number
  speedKt: number
  fuelFraction: number
  bingo: boolean
  throttle: string
  alive: boolean
  waypointsRemaining: number
  /**
   * Sequence of the course order this unit is currently flying; 0 if none has been applied.
   *
   * Needed because `waypointsRemaining === 0` means two different things — "the plan is
   * finished" and "the order has not been ingested yet". Trimming the drawn course on the
   * count alone deleted plans drawn while the clock was paused.
   */
  courseSeq: number
}

export interface Snapshot {
  type: 'snapshot'
  tick: number
  simTimeNs: number
  epochIso: string
  tickSeconds: number
  running: boolean
  compression: number
  /** Ticks the pacer could not fit into the last wake-up. Non-zero means the simulation is
   *  running slower than its stated compression, which the operator needs to know. */
  behindTicks: number
  engineVersion: string
  seed: string
  /** Content address of the dataset this run is using. The status bar has shown a hardcoded
   *  digest since the first prototype; this is the real one. */
  datasetRef?: string
  datasetDigest?: string
  scenarioDigest?: string
  /** Active scenario name and library slug, so the status bar can name the exercise the moment
   *  it changes and the picker can mark which entry is running. */
  scenarioName?: string
  scenarioSlug?: string
  entities: SnapshotEntity[]
  /** BLUE's side picture. Absent from an older Gateway, in which case the fixture stands. */
  contacts?: LiveTrack[]
}

export interface GatewayEvent {
  seq: number
  /** Simulated nanoseconds. Named `t` by the wire format (`orbis_core.Event.to_dict`) — the
   *  stream is unbounded and this key appears on every record, so it is kept short. */
  t: number
  tick: number
  kind: string
  /** Absent, not null, when an event has no subject. */
  subject?: string
  /** Absent when empty. */
  payload?: Record<string, unknown>
}

export interface EventBatch {
  type: 'events'
  events: GatewayEvent[]
}

/**
 * The API is reached SAME-ORIGIN, through nginx's `/v1/` proxy.
 *
 * That is what lets the Deck keep `connect-src 'self'` in its CSP — the air-gapped
 * configuration and the development one are then the same configuration, rather than two that
 * have to be kept in step. It also means no CORS.
 *
 * `VITE_GATEWAY_ORIGIN` exists only for `vite dev`, where the Deck is served from :5173 with no
 * proxy in front of it. It is unset in every build.
 */
const DEV_ORIGIN: string | undefined = import.meta.env.VITE_GATEWAY_ORIGIN

/**
 * Bearer token.
 *
 * Read from `localStorage` so a browser can be given a real identity without rebuilding, falling
 * back to a development token. That fallback is only usable because the Gateway REFUSES `dev-*`
 * credentials under `ORBIS_ENV=production` — so shipping this constant cannot silently
 * authenticate against a real deployment; it fails closed.
 *
 * ## Why the default is EXCON and not an operator
 *
 * It was `dev-operator-blue`, which is the more conservative-looking choice and was wrong. The
 * OPERATOR role deliberately may not control the clock — pausing is an EXCON decision, because one
 * participant pausing stops the exercise for everyone — so the very first thing a visitor does on
 * opening the Deck, press play, was disabled with only a tooltip to explain it.
 *
 * A demo whose primary control is greyed out on arrival reads as broken, not as well-governed. The
 * RBAC model is still fully enforced server-side and is still demonstrable: set
 * `orbis.token` in localStorage to any of the `dev-*` tokens and the UI follows what the Gateway
 * reports for it.
 *
 * ## `VITE_ORBIS_TOKEN` — for a deployment on a real network
 *
 * A public build sets this at build time and the Gateway runs with `ORBIS_ENV=production`, which
 * refuses every `dev-*` credential. The token baked in is deliberately the LOWEST-privilege one:
 * whatever is in the bundle is readable by anyone who opens the developer tools, so it must be a
 * credential that is safe to publish. An observer can watch the exercise and cannot touch it.
 *
 * Anyone who needs to drive the simulation sets `orbis.token` in localStorage to a token that was
 * never shipped. That is the same mechanism as below, used in earnest.
 *
 * A login screen replaces this.
 */
const DEFAULT_DEV_TOKEN: string = import.meta.env.VITE_ORBIS_TOKEN || 'dev-umpire'

export function authToken(): string {
  try {
    return window.localStorage.getItem('orbis.token') || DEFAULT_DEV_TOKEN
  } catch {
    // Private browsing can throw on localStorage access. The dev token is the right fallback.
    return DEFAULT_DEV_TOKEN
  }
}

export function setAuthToken(token: string): void {
  try {
    window.localStorage.setItem('orbis.token', token)
  } catch {
    /* nothing sensible to do; the caller sees the connection state */
  }
}

/** What the Gateway says this token may do. Asked, never assumed. */
export interface WhoAmI {
  user: string
  role: string
  side: string
  mayWrite: boolean
  mayControlTime: boolean
  seesAllSides: boolean
  seesTruth: boolean
}

export async function fetchWhoAmI(): Promise<WhoAmI | null> {
  try {
    const res = await fetch(`${baseUrl()}/v1/whoami`, { headers: authHeaders() })
    return res.ok ? ((await res.json()) as WhoAmI) : null
  } catch {
    return null
  }
}

export function authHeaders(): Record<string, string> {
  return { Authorization: `Bearer ${authToken()}` }
}

export function baseUrl(): string {
  return DEV_ORIGIN ?? ''
}

function wsUrl(): string {
  if (DEV_ORIGIN) return `${DEV_ORIGIN.replace(/^http/, 'ws')}/v1/stream`
  // Derived from the page, so an https deployment gets wss without a second code path.
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${proto}//${window.location.host}/v1/stream`
}

async function post(path: string, body?: unknown): Promise<Response | null> {
  try {
    return await fetch(`${baseUrl()}${path}`, {
      method: 'POST',
      headers: body
        ? { 'Content-Type': 'application/json', ...authHeaders() }
        : authHeaders(),
      body: body ? JSON.stringify(body) : undefined,
    })
  } catch {
    // A failed control call is not worth throwing over: the operator sees the connection
    // indicator, and the next snapshot will show the authoritative state either way. Throwing
    // here would just require every caller to handle an error it cannot act on.
    return null
  }
}

export const gateway = {
  play: () => post('/v1/control/play'),
  pause: () => post('/v1/control/pause'),
  setCompression: (factor: number) => post('/v1/control/compression', { factor }),
  reset: () => post('/v1/control/reset'),

  /** Send a unit's course. An empty waypoint list clears it. */
  setCourse: (entityId: string, waypoints: { lon: number; lat: number }[]) =>
    post('/v1/orders/course', { entityId, waypoints }),
}

/** Static description, fetched once on connect. Carries the dataset-derived identity of each
 *  platform — class name, SIDC and DIS enumeration — so the client stops needing its own copy. */
export interface ScenarioInfo {
  name: string
  epochIso: string
  classification?: string
  dataset?: string
  datasetDigest?: string
  scenarioDigest?: string
  entities: {
    id: string
    designation: string
    kind: string
    side: string
    classRef?: string
    sidc?: string
    dis?: string
    provenance?: string
  }[]
  /* The PLAN: reference points, zones and missions, side-scoped by the Gateway.
   *
   * These used to be authored in `ui/src/data/*.ts`, which made them the last thing on screen the
   * client had made up rather than reported. They belong to the exercise, so a different scenario
   * gets a different plan instead of inheriting the Baltic's patrol boxes.
   *
   * Typed loosely and narrowed by the store, because the Gateway passes them through from the
   * scenario document — a second strict schema on this side would reject a field the author
   * legitimately added. */
  refPoints?: RefPoint[]
  zones?: ExclusionZone[]
  missions?: Mission[]
}

export async function fetchScenario(): Promise<ScenarioInfo | null> {
  try {
    const res = await fetch(`${baseUrl()}/v1/scenario`, { headers: authHeaders() })
    if (!res.ok) return null
    return (await res.json()) as ScenarioInfo
  } catch {
    return null
  }
}

export interface StreamHandlers {
  onSnapshot: (s: Snapshot) => void
  onEvents: (e: GatewayEvent[]) => void
  onState: (s: ConnectionState) => void
}

/**
 * Connect to the snapshot stream, reconnecting with backoff.
 *
 * Backoff rather than a fixed retry interval: a Gateway that is down stays down for seconds or
 * minutes, and hammering it every 500 ms fills the console with failures that hide anything
 * useful. Capped at 5 s so a restarted Gateway is picked up promptly.
 *
 * Returns a disposer. React Strict Mode mounts effects twice in development, so a connection
 * that cannot be cleanly torn down produces two live sockets and double-applied snapshots.
 */
export function connectStream(handlers: StreamHandlers): () => void {
  let socket: WebSocket | null = null
  let retryMs = 500
  let timer = 0
  let disposed = false

  const open = () => {
    if (disposed) return
    handlers.onState('connecting')
    let ws: WebSocket
    try {
      // Authenticated via the SUBPROTOCOL, not a query parameter: browsers cannot set headers on
      // a WebSocket handshake, and `?token=…` would land the credential in nginx's access log,
      // the browser history and any referrer.
      ws = new WebSocket(wsUrl(), ['bearer', authToken()])
    } catch {
      schedule()
      return
    }
    socket = ws

    ws.onopen = () => {
      retryMs = 500
      handlers.onState('live')
    }

    ws.onmessage = (ev) => {
      // Parsing and handling are caught SEPARATELY and on purpose. A single try around both
      // swallowed a real bug in the store — the event field names were wrong, the handler threw
      // on every batch, and because the catch was silent the log simply stayed empty with no
      // console output to explain it. A dropped frame is routine; a throwing handler is not.
      let msg: Snapshot | EventBatch
      try {
        msg = JSON.parse(ev.data as string) as Snapshot | EventBatch
      } catch {
        // Self-correcting: snapshots are full state, so the next frame repairs everything.
        return
      }
      if (msg.type === 'snapshot') handlers.onSnapshot(msg)
      else if (msg.type === 'events') handlers.onEvents(msg.events)
    }

    ws.onclose = (ev) => {
      socket = null
      if (disposed) return
      handlers.onState('offline')
      // 1008 is the Gateway refusing the token. Retrying cannot help and would hammer a server
      // that will keep saying no, so the loop stops and the status bar stays OFFLINE.
      if (ev.code === 1008) return
      schedule()
    }

    // `onerror` always precedes `onclose`, so reconnection is driven from `onclose` alone.
    // Handling both would double the backoff on every failure.
    ws.onerror = () => {}
  }

  const schedule = () => {
    if (disposed) return
    window.clearTimeout(timer)
    timer = window.setTimeout(open, retryMs)
    retryMs = Math.min(retryMs * 2, 5000)
  }

  open()

  return () => {
    disposed = true
    window.clearTimeout(timer)
    if (socket) {
      // Detach before closing: the handler would otherwise fire during teardown and schedule
      // a reconnect for a stream nobody is listening to.
      socket.onclose = null
      socket.close()
      socket = null
    }
  }
}
